'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { PropertyDetailListing } from '../types';
import type { ApartmentReviews, BuildingReviewsSnapshot } from '@/src/lib/api/endpoints/apartmentReviews';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import type { HotpadsClickstreamListing } from '@/src/analytics/hotpads/types';
import { useApartmentReviews } from '@/src/lib/hooks/query/useApartmentReviews';

const ReviewsContext = createContext<{
  listing: HotpadsClickstreamListing | null;
  lotId: string;
  reviews: ApartmentReviews | null;
  googlePlaceId?: string;
  redditTopics: NonNullable<ApartmentReviews['reddit']>['topics'];
}>({
  listing: null,
  lotId: '',
  reviews: null,
  redditTopics: [],
});

export function ApartmentReviewsProvider({
  listing,
  snapshot,
  children,
}: {
  listing: PropertyDetailListing;
  snapshot?: BuildingReviewsSnapshot;
  children: ReactNode;
}) {
  const reviews = useApartmentReviews(listing, snapshot);
  const googlePlaceId = reviews?.google?.placeId;
  const redditTopics = reviews?.reddit?.topics ?? [];
  const lotId = listing.maloneLotIdEncoded ?? '';
  return (
    <ReviewsContext.Provider
      value={{ listing: toHotpadsClickstreamListing(listing), lotId, reviews, googlePlaceId, redditTopics }}
    >
      {children}
    </ReviewsContext.Provider>
  );
}

export const useApartmentReviewsContext = () => useContext(ReviewsContext);
